import type { ReactNode } from "react";
import { useRef, useState } from "react";

import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { Crown } from "@similarweb/icons";
import { LineGraph } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, Button, Card, EmptyState, IconButton, LegendWithOneLineCheckboxFlex } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { Legends } from "app/components/React/Legends/Legends";
import { EGridDirections } from "app/components/React/Legends/legendsTypes";
import { WeShare } from "app/components/weshare/weshare";
import {
    ButtonsContainer,
    ChartContainer,
    ChartSection,
    CheckboxesContainer,
    TabPanelStyled,
    UtilitiesContainer,
} from "app/pages/website-analysis/TrafficAndEngagement/Components/StyledComponents";
import { PopSingleModeLegends } from "app/pages/website-analysis/TrafficAndEngagement/Legends/PopSingleModeLegends";
import {
    getAvailabilityInfoForConversionRates,
    REASON_CONVERSION_RATES_DURATION,
} from "app/pages/website-analysis/TrafficAndEngagement/MD/tabsUtils";
import { pngDownload } from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import DurationService from "app/services/DurationService";
import { openHooksModalV5 } from "app/services/ModalService";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { allTrackers } from "app/services/track/track";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { PngHeader } from "app/UtilitiesAndConstants/UtilitiesComponents/PngHeader";
import { GraphLoader } from "pro-features/components/Loaders/src/ExpandedTableRowLoader/ExpandedTableRowLoader";

import type { ConversionMetric } from "./constants";
import {
    CONVERSION_METRICS,
    CONVERSION_RATE_TRANSLATION_KEYS,
    CONVERSION_TABS,
    CONVERSIONS_TRACKING_PREFIX,
    formatValue,
    HEADLINE_DASH,
    HEADLINE_PASSTHROUGH_FORMAT,
} from "./constants";
import { ConversionRateChartEmptyState } from "./ConversionRateChartEmptyState";
import { ConversionsOverTimeTabs } from "./ConversionsOverTimeTabs";
import { getExcelUrl } from "./conversionRatesUtils";
import type { ConversionsOverTimeWidgetProps, TabItem } from "./types";
import { useConversionRatesChart } from "./useConversionRatesChart";

const HOOK_PLACEHOLDER_SERIES = [
    {
        name: "placeholder",
        id: "placeholder",
        visible: true,
        color: colorsPalettes.blue[400],
        data: [340_000, 320_000, 325_000, 322_000, 310_000, 285_000].map((value, index) => [
            Date.UTC(2020, 11 + index, 1),
            value,
        ]),
    },
];

const BlurredContent = styled.div`
    filter: blur(6px);
    pointer-events: none;
    user-select: none;
`;

// The v2 Crown icon renders a bare svg, so it misses the spacing IconButton applies to legacy icons
const UnlockAccessButton = styled(Button)`
    svg {
        margin-right: 6px;
    }
`;

export const ConversionsOverTimeWidget = (props: ConversionsOverTimeWidgetProps) => {
    const { params, meta, chosenSites, availabilityState } = props;
    const { translate } = useTranslate();
    const chartPngRef = useRef<HTMLDivElement | null>(null);
    const [metric, setMetric] = useState<ConversionMetric>(CONVERSION_METRICS.convertedVisits);
    const [hiddenSites, setHiddenSites] = useState<Set<string>>(() => new Set());
    const isCompareMode = !meta.isSingleMode;
    const isLocked = availabilityState === "locked";
    const webSource = params.webSource ?? devicesTypes.TOTAL;

    const { isAvailable, reason } = getAvailabilityInfoForConversionRates({
        isNonTotalWebSource: meta.isNonTotalWebSource,
        duration: params.duration,
    });
    const enabled = availabilityState === "available" && isAvailable;
    const isUnavailableForParams = availabilityState === "available" && !isAvailable;

    const { query, singleApiParams, graphSeries, graphConfig, hasData, popLegendItems, siteAverages, emptyStateType } =
        useConversionRatesChart({ ...props, metric, enabled, hiddenSites });

    const durations = DurationService.getDurationData(params.duration, params.comparedDuration);

    // Unavailable params have no headline to show; compare mode renders title-only tabs and keeps its averages in the legends
    const headlinePlaceholder = !isAvailable ? HEADLINE_DASH : null;
    const tabs: TabItem[] = CONVERSION_TABS.map((tab) => ({
        ...tab,
        value: headlinePlaceholder ?? query.data?.[0]?.[tab.metric]?.average ?? null,
        format: headlinePlaceholder ? HEADLINE_PASSTHROUGH_FORMAT : tab.format,
    }));
    const selectedTab = tabs.find((tab) => tab.metric === metric) ?? tabs[0];
    const chartTitle = translate(selectedTab.title);
    const placeholderTabs: TabItem[] = CONVERSION_TABS.map((tab) => ({
        ...tab,
        value: tab.metric === CONVERSION_METRICS.conversionRate ? 0.125 : 8_190_000,
    }));

    const compareLegendItems = isCompareMode
        ? graphSeries.map((series) => ({
              name: series.name,
              color: series.color,
              hidden: hiddenSites.has(series.name),
              ...(meta.isPOP ? {} : { data: formatValue(siteAverages[series.name] ?? 0, metric) }),
          }))
        : [];

    const toggleSeries = (legendItem: { name: string }, hidden: boolean) => {
        setHiddenSites((prev) => {
            const next = new Set(prev);
            next[hidden ? "delete" : "add"](String(legendItem.name));
            return next;
        });
    };

    const handleTabSelect = (tab: TabItem) => {
        allTrackers.dispatchTrackEvent("Metric Button", "click", `${CONVERSIONS_TRACKING_PREFIX}/${tab.metric}`);
        setMetric(tab.metric);
    };

    const handleExcelClick = () => {
        allTrackers.dispatchTrackEvent("Download", "submit-ok", `Table_ConversionsOverTime/Excel/${chartTitle}`);
    };

    const handlePngDownload = () => {
        if (chartPngRef.current) {
            return pngDownload(chartPngRef.current, chartTitle, () =>
                allTrackers.dispatchTrackEvent("Download", "submit-ok", `${CONVERSIONS_TRACKING_PREFIX}/PNG`),
            );
        }
    };

    if (availabilityState === "hidden") {
        return null;
    }

    const renderChartContent = () => {
        if (query.isLoading) {
            return (
                <GraphLoader
                    width={window.innerWidth > 1440 ? 1280 : 1000}
                    height={288}
                />
            );
        }

        if (query.isError || !hasData) {
            return <ConversionRateChartEmptyState emptyStateType={emptyStateType} />;
        }

        const chart = (
            <LineGraph
                withMarkers
                data={graphSeries}
                shouldPresentLegend={false}
                graphConfig={graphConfig}
            />
        );

        const withLegends = (legends: ReactNode) => (
            <Flex>
                <ChartContainer>{chart}</ChartContainer>
                <CheckboxesContainer>
                    <Box padding="0 10px 0 0">{legends}</Box>
                </CheckboxesContainer>
            </Flex>
        );

        if (isCompareMode) {
            return withLegends(
                <Legends
                    legendItems={compareLegendItems}
                    toggleSeries={toggleSeries}
                    gridDirection={EGridDirections.COLUMN}
                    legendsGap="8px"
                    legendComponent={LegendWithOneLineCheckboxFlex}
                    textMaxWidth="150px"
                />,
            );
        }

        if (!meta.isPOP) {
            return chart;
        }

        return withLegends(
            <PopSingleModeLegends
                legendItems={popLegendItems}
                webSource={webSource}
                chartType="line"
                customLegends={true}
            />,
        );
    };

    const renderBody = () => {
        if (isLocked) {
            return (
                <Box
                    position="relative"
                    data-automation="conversions-over-time-locked"
                >
                    <BlurredContent aria-hidden="true">
                        <ConversionsOverTimeTabs
                            tabs={placeholderTabs}
                            selectedTab={placeholderTabs[0]}
                            isCompareMode={isCompareMode}
                            onSelect={() => undefined}
                        >
                            <TabPanelStyled>
                                <ChartSection>
                                    <LineGraph
                                        withMarkers
                                        data={HOOK_PLACEHOLDER_SERIES}
                                        shouldPresentLegend={false}
                                        graphConfig={graphConfig}
                                    />
                                </ChartSection>
                            </TabPanelStyled>
                        </ConversionsOverTimeTabs>
                    </BlurredContent>
                    <Flex
                        position="absolute"
                        top="0"
                        right="0"
                        bottom="0"
                        left="0"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        gap="16px"
                    >
                        <Text
                            fontVariant="subtitle2"
                            color={colorsPalettes.carbon[500]}
                            textAlign="center"
                        >
                            {translate(CONVERSION_RATE_TRANSLATION_KEYS.hookTitle)}
                        </Text>
                        <UnlockAccessButton
                            type="trial"
                            label={translate(CONVERSION_RATE_TRANSLATION_KEYS.hookCta)}
                            onClick={() => openHooksModalV5("conversionRate")}
                            data-automation="conversions-over-time-unlock-cta"
                        >
                            <Crown
                                size="xs"
                                color={colorsPalettes.carbon[0]}
                            />
                        </UnlockAccessButton>
                    </Flex>
                </Box>
            );
        }

        if (!isAvailable) {
            // Only the 28-day case has designed copy; other blockers keep their existing reason line as the subtitle
            const subtitle =
                reason === REASON_CONVERSION_RATES_DURATION
                    ? CONVERSION_RATE_TRANSLATION_KEYS.emptyUnavailableDurationSubtitle
                    : (reason ?? "");

            return (
                <ConversionsOverTimeTabs
                    tabs={tabs}
                    selectedTab={selectedTab}
                    isCompareMode={isCompareMode}
                    onSelect={handleTabSelect}
                >
                    {tabs.map((tab) => (
                        <TabPanelStyled key={tab.metric}>
                            <ChartSection>
                                <Flex
                                    justifyContent="center"
                                    padding="40px 24px"
                                    data-automation="conversions-over-time-unavailable"
                                >
                                    <EmptyState
                                        title={translate(CONVERSION_RATE_TRANSLATION_KEYS.emptyUnavailableTitle)}
                                        subtitle={translate(subtitle)}
                                    />
                                </Flex>
                            </ChartSection>
                        </TabPanelStyled>
                    ))}
                </ConversionsOverTimeTabs>
            );
        }

        return (
            <ConversionsOverTimeTabs
                tabs={tabs}
                selectedTab={selectedTab}
                isCompareMode={isCompareMode}
                onSelect={handleTabSelect}
            >
                {tabs.map((tab) => (
                    <TabPanelStyled key={tab.metric}>
                        <div
                            ref={chartPngRef}
                            data-automation="conversion-rates-chart"
                        >
                            <PngHeader
                                metricTitle={chartTitle}
                                durations={durations.forWidget}
                                webSource={webSource}
                                country={Number(params.country) || 999}
                            />
                            <UtilitiesContainer legendsAmount={1}>
                                <ButtonsContainer legendsAmount={1}>
                                    <DownloadButtonMenu
                                        PNG={true}
                                        handleDownloadFunction={handlePngDownload}
                                    />
                                    <WeShare
                                        chartRef={chartPngRef.current}
                                        description={chartTitle}
                                        title={`${chosenSites?.[0] || "website"} ${chartTitle}`}
                                        redirectUrl={`https://www.similarweb.com/website/${chosenSites?.[0] || ""}/#overview`}
                                        isHighChart={false}
                                    />
                                </ButtonsContainer>
                            </UtilitiesContainer>
                            <ChartSection>{renderChartContent()}</ChartSection>
                        </div>
                    </TabPanelStyled>
                ))}
            </ConversionsOverTimeTabs>
        );
    };

    return (
        <Card data-automation="conversions-over-time-widget">
            <Card.Header
                withDivider
                justifyContent="space-between"
                alignItems="center"
            >
                <Card.Title>
                    <Flex
                        gap="8px"
                        alignItems="center"
                    >
                        <Text
                            fontVariant="h5_2"
                            data-automation="conversions-over-time-title"
                        >
                            {translate(CONVERSION_RATE_TRANSLATION_KEYS.widgetTitle)}
                        </Text>
                        <Badge
                            type="new"
                            data-automation="conversions-over-time-badge"
                        />
                    </Flex>
                </Card.Title>
                {enabled ? (
                    <DownloadButtonMenu
                        Excel={true}
                        downloadUrl={getExcelUrl(singleApiParams)}
                        trackingFunction={handleExcelClick}
                        excelLocked={!swSettings.current.resources.IsExcelAllowed}
                        PaidExcel={isPaidExcelAllowed()}
                        totalCount={query.data?.[0]?.[metric]?.data.length}
                    />
                ) : null}
                {isUnavailableForParams ? (
                    <IconButton
                        type="flat"
                        iconName="excel"
                        isDisabled={true}
                        data-automation="conversions-over-time-excel-disabled"
                    />
                ) : null}
            </Card.Header>
            {renderBody()}
        </Card>
    );
};
